import { FunctionComponent, useCallback, useEffect, useMemo, useState } from "react";
import { Get } from "../../utils/api";
import LogoutButton from "../navigation/LogoutButton";
import { ErrorIcon, VerboseIcon, WarningIcon } from "../shared/Icon";
import "./HealthBar.css";

interface HealthMetrics {
	metrics: HealthData[];
}

interface HealthData {
	service: string;
	healthData: any;
	lastUpdated: string;
}

interface HealthCheck<TData> {
	Data: TData | null;
	Exception: {
		Message: string;
	} | null;
}

type InternetHealth = HealthCheck<{
	UploadSpeedKbps: number;
	DownloadSpeedKbps: number;
	Latency: number;
	Server: {
		Sponsor: string;
	};
}>;

type WindowsMetrics = HealthCheck<{
	PercentProcessorTime: number;
	AvailableMBytes: number;
	AllDrives: {
		Name: string;
		VolumeLabel: string;
		AvailableFreeSpaceGB: number;
		TotalSizeGB: number;
	}[];
}>;

interface NasHealth {
	CanReachNas: boolean;
	IsNasHealthy: boolean | null;
	HealthMessage: string | null;
	AvailableFreeSpaceGB: number;
	TotalSizeGB: number;
}

interface WebServerHealth {
	data: {
		InternetHealth: InternetHealth;
		WindowsMetrics: WindowsMetrics;
		LastDatabaseBackup: HealthCheck<string>;
		NasHealth: HealthCheck<NasHealth>;
		XlNasHealth: HealthCheck<NasHealth>;
	}
	lastUpdated: Date;
}

interface ServiceServerHealth {
	data: {
		InternetHealth: InternetHealth;
		WindowsMetrics: WindowsMetrics;
		IsConnectedToVpn: HealthCheck<boolean>;
	}
	lastUpdated: Date;
}

enum MessageLevel {
	Verbose = 0,
	Warning = 1,
	Error = 2,
}

interface IProps {
	logOut(): void;
}

const HealthBar: FunctionComponent<IProps> = (props) => {
	const { logOut } = props;
	const [webServerMetrics, setWebServerMetrics] = useState<WebServerHealth>();
	const [serviceServerMetrics, setServiceServerMetrics] = useState<ServiceServerHealth>();
	const [error, setError] = useState<string>();

	const loadHealthMetrics = useCallback((retryCount: number = 0) => {
		Get<HealthMetrics>("/health/getHealthMetrics")
			.then(result => {
				const webServerData = result.metrics.filter(x => x.service === "WebServer")[0];
				setWebServerMetrics({
					data: JSON.parse(webServerData.healthData),
					lastUpdated: new Date(webServerData.lastUpdated + "Z"),
				});

				const serviceServerData = result.metrics.filter(x => x.service === "ServiceServer")[0];
				setServiceServerMetrics({
					data: JSON.parse(serviceServerData.healthData),
					lastUpdated: new Date(serviceServerData.lastUpdated + "Z"),
				});

				setError(undefined);
			})
			.catch(error => {
				if(error?.message === "Unauthorized") {
					logOut();
					return;
				}
				if (retryCount < 2) {
					setTimeout(() => loadHealthMetrics(retryCount + 1), 2000);
					return;
				}
				setError(error?.message);
			});
	}, [logOut]);

	useEffect(() => {
		loadHealthMetrics();

		let timeout: NodeJS.Timeout | null = null;
		const loadForever = () => {
			loadHealthMetrics();
			timeout = setTimeout(loadForever, 30000);
		}
		timeout = setTimeout(loadForever, 30000);

		return () => {if (timeout !== null) { clearTimeout(timeout); }};
	}, [loadHealthMetrics]);

	const message = useMemo(() => {
		if (error) {
			return {
				level: MessageLevel.Error,
				message: "Couldn't load Maidchan's health vitals.",
			}
		}
		if (!webServerMetrics || !serviceServerMetrics) { return null; }

		const now = new Date();
		if ((now.getTime() - webServerMetrics.lastUpdated.getTime()) > 30 * 60 * 1000) {
			return {
				level: MessageLevel.Error,
				message: `Health vitals have not been updated since ${webServerMetrics.lastUpdated.toLocaleString()}.`,
			};
		}

		const nasHealth = [webServerMetrics.data.NasHealth, webServerMetrics.data.XlNasHealth];

		// Lost connection to all network storage
		if (nasHealth.every(x => !x.Data || !x.Data.CanReachNas)) {
			return {
				level: MessageLevel.Error,
				message: "Maidchan has lost connection to the network storage. Images and video may not load.",
			}
		}

		// Lost connection to one network storage
		if (nasHealth.some(x => !x.Data || !x.Data.CanReachNas)) {
			return {
				level: MessageLevel.Error,
				message: "Maidchan has partial connection to the network storage. Images and video may not load.",
			}
		}

		const messages = [];
		// What if error retrieving last backup

		const lastDatabaseBackup =  new Date(webServerMetrics.data.LastDatabaseBackup.Data as string);
		if ((now.getTime() - lastDatabaseBackup.getTime()) > 3 * 24 * 60 * 60 * 1000) {
			messages.push({
				level: ((now.getTime()) - lastDatabaseBackup.getTime() > 7 * 24 * 60 * 60 * 1000) ? MessageLevel.Error : MessageLevel.Warning,
				message: `Maidchan's database has not been backed up since ${lastDatabaseBackup.toLocaleString()}. Contact an admin to fix this.`,
			});
		}

		// NAS starting to die
		if (nasHealth.some(x => x.Data?.IsNasHealthy === false)) {
			messages.push({
				level: MessageLevel.Warning,
				message: "Maidchan's network storage is reporting not healthy. Contact an admin to investigate.",
			});
		}

		let totalFreeStorage = 0;
		nasHealth.forEach(x => { totalFreeStorage += x.Data?.AvailableFreeSpaceGB ?? 0; })
		if (totalFreeStorage < 4000) {
			messages.push({
				level: totalFreeStorage < 1000 ? MessageLevel.Error : MessageLevel.Warning,
				message: `Maidchan's network storage is running low. ${totalFreeStorage.toFixed(0)}GB remaining.`
			});
		}
		else {
			nasHealth.forEach((x, i) => {
				const freeStorage = x.Data?.AvailableFreeSpaceGB ?? 0;
				if (freeStorage < 1000) {
					const name = i === 0 ? "main" : "secondary";

					messages.push({
						level: MessageLevel.Error,
						message: `Maidchan's ${name} network storage is running low. ${freeStorage.toFixed(1)}GB remaining.`
					});
				}
			});
		}

		// Web Server C Drive
		// console.log(webServerMetrics, serviceServerMetrics);
		if (webServerMetrics.data && webServerMetrics.data.WindowsMetrics.Data?.AllDrives) {
			const cDrive = webServerMetrics.data.WindowsMetrics.Data.AllDrives.find(d => d.Name === "C:\\");
			if (cDrive && cDrive.AvailableFreeSpaceGB < 5) {
				messages.push({
					level: cDrive.AvailableFreeSpaceGB < 1 ? MessageLevel.Error : MessageLevel.Warning,
					message: `Maidchan's web server storage is running low. ${cDrive.AvailableFreeSpaceGB.toFixed(1)}GB remaining.`
				});
			}
		}
		if (serviceServerMetrics.data && serviceServerMetrics.data.WindowsMetrics.Data?.AllDrives) {
			const cDrive = serviceServerMetrics.data.WindowsMetrics.Data.AllDrives.find(d => d.Name === "C:\\");
			if (cDrive && cDrive.AvailableFreeSpaceGB < 5) {
				messages.push({
					level: cDrive.AvailableFreeSpaceGB < 1 ? MessageLevel.Error : MessageLevel.Warning,
					message: `Maidchan's service server storage is running low. ${cDrive.AvailableFreeSpaceGB.toFixed(1)}GB remaining.`
				});
			}
		}

		// if (!webServerMetrics.data.InternetHealth.Data) {
		// 	return {
		// 		level: MessageLevel.Error,
		// 		message: "Can't determine Maidchan's internet health. May experience issues streaming."
		// 	};
		// }

		// const uploadSpeed = (webServerMetrics.data.InternetHealth.Data?.UploadSpeedKbps ?? 0) / 1000;
		// if (uploadSpeed < 7) {
		// 	messages.push({
		// 		level: uploadSpeed < 2 ? MessageLevel.Error : MessageLevel.Warning,
		// 		message: `Maidchan's upload speed is low. ${uploadSpeed.toFixed(2)}Mbps. May experience issues streaming.`
		// 	});
		// }

		if ((now.getTime() - webServerMetrics.lastUpdated.getTime()) > 30 * 60 * 1000) {
			messages.push({
				level: MessageLevel.Verbose,
				message: `Anime upload service stopped filing health metrics since ${webServerMetrics.lastUpdated.toLocaleString()}. New shows may not be added.`,
			});
		}
		else if (!serviceServerMetrics.data.IsConnectedToVpn.Data) {
			messages.push({
				level: MessageLevel.Verbose,
				message: "Anime upload service lost connection to VPN. New shows will not be added. Contact an admin to fix this.",
			});
		}

		if (messages.length === 0) { return null; }
		
		return {
			level: messages.some(x => x.level === MessageLevel.Error) ? MessageLevel.Error : messages.some(x => x.level === MessageLevel.Warning) ? MessageLevel.Warning : MessageLevel.Verbose,
			messages: messages.sort((x,y) => y.level - x.level).map(x => x),
		}
	}, [error, serviceServerMetrics, webServerMetrics]);

	const messages = useMemo(() => {
		if (!message) { return null; }
		if (message.message) {
			return <div>{getMessageIcon(message.level)}{" "}{message.message}</div>;
		}
		return <>
			{message.messages?.map(x => {
				return <div key={x.message}>{getMessageIcon(x.level)}{" "}{x.message}</div>;
			})}
		</>;
	}, [message]);

	const messageClass = useMemo(() => {
		if (!message) { return; }
		switch(message.level) {
			case MessageLevel.Error: return "errorMessage";
			case MessageLevel.Warning: return "warningMessage";
			case MessageLevel.Verbose: return "verboseMessage";
		}
	}, [message]);

	return <div className={messageClass}>
		{messages}	
	</div>;
}

const getMessageIcon = (level: MessageLevel) => {
	switch(level) {
		case MessageLevel.Error: return <ErrorIcon />;
		case MessageLevel.Warning: return <WarningIcon />;
		case MessageLevel.Verbose: return <VerboseIcon />;
	}
};

export default HealthBar;